import { mergePDFs, splitPDF, rotatePDFPages, compressPDF, addWatermark } from './pdfEditorUtils';
import { convertDocument } from './conversionUtils';
import { extractInvoiceDataBulk } from './invoiceExtractionUtils';
import { detectFormFields } from './formDetectionUtils';

/**
 * Bulk PDF Tools for Freelancers/Agencies
 * Process multiple PDFs simultaneously
 */

// Bulk merge PDFs
export const bulkMergePDFs = async (fileGroups, onProgress) => {
  const results = [];
  const totalGroups = fileGroups.length;
  
  for (let i = 0; i < fileGroups.length; i++) {
    const group = fileGroups[i];
    const groupProgress = (i / totalGroups) * 100;
    
    try {
      const result = await mergePDFs(group.files, (progress) => {
        onProgress(groupProgress + (progress * (1 / totalGroups)));
      });
      
      results.push({
        groupName: group.name || `Group ${i + 1}`,
        success: true,
        ...result,
      });
    } catch (error) {
      results.push({
        groupName: group.name || `Group ${i + 1}`,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};

// Bulk split PDFs
export const bulkSplitPDFs = async (files, splitOptions, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / totalFiles) * 100;
    
    try {
      const splitResults = await splitPDF(file, splitOptions, (progress) => {
        onProgress(fileProgress + (progress * (1 / totalFiles)));
      });
      
      results.push({
        filename: file.name,
        success: true,
        files: splitResults,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};

// Bulk convert PDFs
export const bulkConvertPDFs = async (files, conversionType, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / totalFiles) * 100;
    
    try {
      const result = await convertDocument(file, conversionType, (progress) => {
        onProgress(fileProgress + (progress * (1 / totalFiles)));
      });
      
      results.push({
        filename: file.name,
        success: true,
        ...result,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};

// Bulk compress PDFs
export const bulkCompressPDFs = async (files, compressionLevel, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / totalFiles) * 100;
    
    try {
      const result = await compressPDF(file, compressionLevel, (progress) => {
        onProgress(fileProgress + (progress * (1 / totalFiles)));
      });
      
      // Calculate compression ratio
      const originalSize = file.size;
      const compressedSize = result.blob.size;
      const compressionRatio = ((originalSize - compressedSize) / originalSize * 100).toFixed(2);
      
      results.push({
        filename: file.name,
        success: true,
        ...result,
        originalSize,
        compressedSize,
        compressionRatio: `${compressionRatio}%`,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};

// Bulk add watermark
export const bulkAddWatermark = async (files, watermarkText, options, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / totalFiles) * 100;
    
    try {
      const result = await addWatermark(file, watermarkText, options, (progress) => {
        onProgress(fileProgress + (progress * (1 / totalFiles)));
      });
      
      results.push({
        filename: file.name,
        success: true,
        ...result,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};

// Bulk rotate PDFs
export const bulkRotatePDFs = async (files, rotation, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / totalFiles) * 100;
    
    try {
      const result = await rotatePDFPages(file, rotation, null, (progress) => {
        onProgress(fileProgress + (progress * (1 / totalFiles)));
      });
      
      results.push({
        filename: file.name,
        success: true,
        ...result,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};

// Bulk form detection
export const bulkDetectForms = async (files, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / totalFiles) * 100;
    
    try {
      const formData = await detectFormFields(file, (progress) => {
        onProgress(fileProgress + (progress * (1 / totalFiles)));
      });
      
      results.push({
        filename: file.name,
        success: true,
        ...formData,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};

// Bulk invoice extraction
export const bulkExtractInvoices = async (files, onProgress) => {
  return await extractInvoiceDataBulk(files, onProgress);
};

// Process queue with priority
export const processQueue = async (queue, onProgress, onItemComplete) => {
  const results = [];
  const totalItems = queue.length;
  
  // Sort by priority if provided
  const sortedQueue = queue.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  
  for (let i = 0; i < sortedQueue.length; i++) {
    const item = sortedQueue[i];
    const itemProgress = (i / totalItems) * 100;
    
    try {
      let result;
      
      switch (item.type) {
        case 'merge':
          result = await mergePDFs(item.files, (progress) => {
            onProgress(itemProgress + (progress * (1 / totalItems)));
          });
          break;
        case 'split':
          result = await splitPDF(item.file, item.options, (progress) => {
            onProgress(itemProgress + (progress * (1 / totalItems)));
          });
          break;
        case 'convert':
          result = await convertDocument(item.file, item.conversionType, (progress) => {
            onProgress(itemProgress + (progress * (1 / totalItems)));
          });
          break;
        case 'compress':
          result = await compressPDF(item.file, item.compressionLevel, (progress) => {
            onProgress(itemProgress + (progress * (1 / totalItems)));
          });
          break;
        case 'watermark':
          result = await addWatermark(item.file, item.watermarkText, item.options, (progress) => {
            onProgress(itemProgress + (progress * (1 / totalItems)));
          });
          break;
        default:
          throw new Error(`Unknown queue item type: ${item.type}`);
      }
      
      results.push({
        id: item.id,
        success: true,
        ...result,
      });
      
      if (onItemComplete) {
        onItemComplete(item.id, true, result);
      }
    } catch (error) {
      results.push({
        id: item.id,
        success: false,
        error: error.message,
      });
      
      if (onItemComplete) {
        onItemComplete(item.id, false, error);
      }
    }
  }
  
  onProgress(100);
  return results;
};

// Get processing statistics
export const getProcessingStats = (results) => {
  const stats = {
    total: results.length,
    successful: 0,
    failed: 0,
    totalSize: 0,
    processedSize: 0,
  };
  
  results.forEach(result => {
    if (result.success) {
      stats.successful++;
      if (result.originalSize) {
        stats.totalSize += result.originalSize;
      }
      if (result.compressedSize) {
        stats.processedSize += result.compressedSize;
      } else if (result.blob) {
        stats.processedSize += result.blob.size;
      }
    } else {
      stats.failed++;
    }
  });
  
  stats.successRate = ((stats.successful / stats.total) * 100).toFixed(2);
  stats.sizeReduction = stats.totalSize > 0 
    ? (((stats.totalSize - stats.processedSize) / stats.totalSize) * 100).toFixed(2)
    : 0;
  
  return stats;
};


